/* Tuner-style needle gauge (−50..+50 cents). Updated imperatively at frame rate through its handle,
   so mic-driven pages don't re-render React on every frame. */
import { type Ref, useImperativeHandle, useRef } from 'react';
import { clamp, fmtDec } from '../core/util';

export interface GaugeUpdate {
  /** live reading: needle and colour follow `cents` */
  active: boolean;
  /** frozen last reading (shown dimmed) when the sound has faded */
  held?: boolean;
  main?: string;
  sub?: string;
  cents?: number | null;
  hz?: number | null;
  /** in-tune tolerance in cents (default 5) */
  tol?: number;
  /** Override "in tune" (callers with hysteresis decide themselves). */
  inTune?: boolean;
  /** Override the status line. */
  status?: string;
  /** false: draw `cents` as given (caller already smooths). Default true. */
  smooth?: boolean;
}

export interface GaugeHandle {
  update(o: GaugeUpdate): void;
}

const W = 330;
const H = 168;
const CX = 165;
const CY = 150;
const R = 122;
const ang = (c: number) => ((-90 + c * 1.6) * Math.PI) / 180;
const pt = (c: number, r: number): [number, number] => [CX + r * Math.cos(ang(c)), CY + r * Math.sin(ang(c))];
const arc = (c0: number, c1: number, r: number) => {
  const [ax, ay] = pt(c0, r);
  const [bx, by] = pt(c1, r);
  return `M${ax} ${ay} A${r} ${r} 0 0 1 ${bx} ${by}`;
};

export function defaultStatus(cents: number, inTune: boolean): string {
  if (inTune) return 'Tam';
  return cents < 0 ? `Pes · ${Math.round(cents)} sent` : `Tiz · +${Math.round(cents)} sent`;
}

export function Gauge({ ref, main = '—', sub = '' }: { ref?: Ref<GaugeHandle>; main?: string; sub?: string }) {
  const root = useRef<HTMLDivElement>(null);
  const needle = useRef<SVGLineElement>(null);
  const mainEl = useRef<HTMLDivElement>(null);
  const subEl = useRef<HTMLDivElement>(null);
  const hzEl = useRef<HTMLDivElement>(null);
  const statusEl = useRef<HTMLDivElement>(null);
  const cur = useRef(0);

  useImperativeHandle(
    ref,
    () => ({
      update(o) {
        const el = root.current;
        if (!el) return;
        const showing = o.active || !!o.held;
        el.classList.toggle('active', showing);
        el.classList.toggle('held', !!o.held && !o.active);
        el.dataset.state = o.active ? 'live' : o.held ? 'held' : 'idle';
        if (o.main != null && mainEl.current) mainEl.current.textContent = o.main;
        if (o.sub != null && subEl.current) subEl.current.textContent = o.sub;
        if (hzEl.current) hzEl.current.textContent = showing && o.hz ? `${fmtDec(o.hz, 1)} Hz` : '';
        const c = showing && o.cents != null ? clamp(o.cents, -50, 50) : 0;
        if (o.smooth === false) cur.current = showing ? c : cur.current * 0.85;
        else
          cur.current = o.active ? cur.current + (c - cur.current) * 0.35 : o.held ? cur.current : cur.current * 0.85;
        needle.current?.setAttribute('transform', `rotate(${cur.current * 1.6} ${CX} ${CY})`);
        const cents = o.cents ?? 0;
        const inTune = showing && o.cents != null && (o.inTune ?? Math.abs(cents) <= (o.tol || 5));
        el.classList.toggle('in-tune', inTune);
        el.dataset.cents = showing && o.cents != null ? cents.toFixed(1) : '';
        el.dataset.inTune = inTune ? 'true' : 'false';
        if (statusEl.current)
          statusEl.current.textContent = !showing || o.cents == null ? '' : (o.status ?? defaultStatus(cents, inTune));
      },
    }),
    [],
  );

  const ticks = [];
  for (let c = -50; c <= 50; c += 10) {
    const [ax, ay] = pt(c, R - 10);
    const [bx, by] = pt(c, R + (c % 50 === 0 ? 8 : 4));
    ticks.push(<line key={c} x1={ax} y1={ay} x2={bx} y2={by} className={`g-tick${c === 0 ? ' zero' : ''}`} />);
  }
  const lbl = (c: number, t: string) => {
    const [x, y] = pt(c, R + 20);
    return (
      <text key={t} x={x} y={y + 4} className="g-lbl">
        {t}
      </text>
    );
  };
  return (
    <div className="gauge" ref={root} data-state="idle" data-testid="gauge">
      <svg viewBox={`0 0 ${W} ${H}`} className="gauge-svg" aria-hidden="true">
        <path d={arc(-50, 50, R)} className="g-track" />
        <path d={arc(-5, 5, R)} className="g-zone" />
        {ticks}
        {lbl(-50, '−50')}
        {lbl(0, '0')}
        {lbl(50, '+50')}
        <line ref={needle} x1={CX} y1={CY} x2={CX} y2={CY - R + 14} className="g-needle" />
        <circle cx={CX} cy={CY} r={6} className="g-hub" />
      </svg>
      <div className="g-readout">
        <div className="g-main" ref={mainEl}>
          {main}
        </div>
        <div className="g-sub" ref={subEl}>
          {sub}
        </div>
      </div>
      <div className="g-meta">
        <div className="g-hz" ref={hzEl} />
        <div className="g-status" ref={statusEl} />
      </div>
    </div>
  );
}
